import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Divider, Form, Input, InputNumber, Modal, Radio, Select, Table, Tabs, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { DatePicker } from "antd";
import * as dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { DELIVERY_DATE_WARNING_DAYS } from "constants/DeliveryConstants";
import { useAuth, useProducts, useSupplements } from "../../../../data/hooks";
import { formatPrice } from "../../../../functions/formatters";

const DateWarningMessage = styled.p`
  color: #ff7300;
`;

const lineKey = (line) => `${line.productId || line.name}::${line.variation?.weight || ""}`;

const formatCents = (cents) => formatPrice(cents / 100);

/**
 * Diff two product line-item lists by (productId, variation) key and return the
 * added/removed quantities needed to go from `original` to `edited`. A quantity
 * increase on an existing line is treated as "added" for the delta amount, and a
 * decrease as "removed" - so partial-quantity edits are handled the same way as
 * adding/removing a line entirely.
 */
const diffProductLines = (original, edited) => {
  const byKey = new Map();
  original.forEach((line) => {
    const key = lineKey(line);
    byKey.set(key, { line, originalQty: line.quantity || 0, editedQty: 0 });
  });
  edited.forEach((line) => {
    const key = lineKey(line);
    if (byKey.has(key)) {
      byKey.get(key).editedQty = line.quantity || 0;
    } else {
      byKey.set(key, { line, originalQty: 0, editedQty: line.quantity || 0 });
    }
  });

  const addedItems = [];
  const removedItems = [];
  byKey.forEach(({ line, originalQty, editedQty }) => {
    const delta = editedQty - originalQty;
    const item = {
      productId: line.productId || null,
      name: line.name,
      unitPriceCents: Math.round((line.paymentPrice || 0) * 100),
      variation: line.variation?.weight || null,
    };
    if (delta > 0) addedItems.push({ ...item, quantity: delta });
    else if (delta < 0) removedItems.push({ ...item, quantity: -delta });
  });
  return { addedItems, removedItems };
};

const summarizeItems = (items) =>
  items.map((item) => `${item.quantity}x ${item.name}${item.variation ? ` (${item.variation}g)` : ""}`).join(", ");

/**
 * Shops whose orders may have their product lines edited here. Editing products can create a
 * nabetaling, which needs a Pay. payment link + webhook handling in that shop's own `api` -
 * both VCE and HVO have that. A shop only belongs here once its api has supplement support;
 * customer details and delivery stay editable for every shop, since those never touch money.
 */
const PRODUCT_EDITING_SUPPORTED_SHOPS = ["VCE", "HVO"];

const CUSTOMER_FIELD_LABELS = {
  firstName: "Voornaam",
  lastName: "Achternaam",
  email: "E-mailadres",
  phoneNumber: "Telefoonnummer",
  street: "Straat",
  houseNumber: "Huisnummer",
  addition: "Toevoeging",
  zipCode: "Postcode",
  city: "Plaats",
};
const CUSTOMER_ADDRESS_FIELDS = ["street", "houseNumber", "addition", "zipCode", "city"];

const originalCustomerFieldValue = (order, field) =>
  CUSTOMER_ADDRESS_FIELDS.includes(field) ? order.customer?.address?.delivery?.[field] : order.customer?.[field];

/** Diff the customer Form values against the order as it was when the modal opened. */
const getCustomerFieldChanges = (order, values) =>
  Object.keys(CUSTOMER_FIELD_LABELS)
    .filter((field) => (originalCustomerFieldValue(order, field) || "") !== (values[field] || ""))
    .map((field) => ({ field, from: originalCustomerFieldValue(order, field) || null, to: values[field] || null }));

/** Diff the delivery Form values against the order as it was when the modal opened. */
const getDeliveryChanges = (order, values) => {
  const changes = [];
  if (values.deliveryType && values.deliveryType !== order.delivery?.type) {
    changes.push({ field: "Verzendmethode", from: order.delivery?.type || null, to: values.deliveryType });
  }
  const newDate = values.deliveryDate ? dayjs(values.deliveryDate).format(DATE_FORMAT_DD_MM_YYYY) : null;
  if (newDate && newDate !== order.delivery?.date) {
    changes.push({ field: "Bezorgdatum", from: order.delivery?.date || null, to: newDate });
  }
  if (
    values.deliveryType === "pickup" &&
    values.pickupLocation &&
    values.pickupLocation !== order.delivery?.pickupLocation
  ) {
    changes.push({ field: "Afhaallocatie", from: order.delivery?.pickupLocation || null, to: values.pickupLocation });
  }
  return changes;
};

const OrderEditModal = ({ visible, order, onClose, updateOrder, refreshOrders }) => {
  const { t } = useTranslation();
  const { email: adminEmail, uid: adminUid } = useAuth();
  const { getProductsByShop } = useProducts();
  const {
    getOpenAdditionalPaymentSupplement,
    addHistoryEntry,
    createSupplement,
    updateSupplement,
    createPaymentLinkForSupplement,
    cancelSupplementPayment,
    sendSupplementPaymentMail,
  } = useSupplements();

  const [form] = Form.useForm();
  const [deliveryType, setDeliveryType] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [editedProducts, setEditedProducts] = useState([]);
  const [newProductId, setNewProductId] = useState(null);
  const [newVariationWeight, setNewVariationWeight] = useState(null);
  const [newQuantity, setNewQuantity] = useState(1);
  const [openSupplement, setOpenSupplement] = useState(undefined); // undefined = still loading
  const [openSupplementCheckError, setOpenSupplementCheckError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Bumped on every form field change so the summary below the tabs recomputes live -
  // antd's Form store doesn't itself trigger a re-render of the component around it.
  const [formVersion, setFormVersion] = useState(0);

  const shopProducts = getProductsByShop(order?.shopSource);

  /**
   * Only tomorrow onwards can be chosen. Customers can already only order for
   * tomorrow or later, so a new payment can never land in a pick day that is
   * already being picked - but a staff member editing an order could otherwise
   * still move it into today or the past, past the Picqer lock.
   *
   * The order's own current date stays selectable, so opening and saving an
   * existing order without touching the date never fails validation.
   */
  const isDeliveryDateDisabled = (current) => {
    if (!current) return false;
    const currentOrderDate = order?.delivery?.date ? dayjs(order.delivery.date, DATE_FORMAT_DD_MM_YYYY) : null;
    if (currentOrderDate && current.isSame(currentOrderDate, "day")) {
      return false;
    }
    return current.isBefore(dayjs().add(1, "day").startOf("day"));
  };
  const selectedCatalogProduct = shopProducts.find((product) => product.id === newProductId);
  const productEditingSupported = PRODUCT_EDITING_SUPPORTED_SHOPS.includes(order?.shopSource);

  useEffect(() => {
    if (!visible || !order) return;

    setDeliveryType(order.delivery?.type);
    setDeliveryDate(dayjs(order.delivery?.date, DATE_FORMAT_DD_MM_YYYY));
    setEditedProducts((order.products || []).map((product) => ({ ...product })));
    form.setFieldsValue({
      deliveryType: order.delivery?.type,
      deliveryDate: dayjs(order.delivery?.date, DATE_FORMAT_DD_MM_YYYY),
      pickupLocation: order.delivery?.pickupLocation || null,
      firstName: order.customer?.firstName,
      lastName: order.customer?.lastName,
      email: order.customer?.email,
      phoneNumber: order.customer?.phoneNumber,
      street: order.customer?.address?.delivery?.street,
      houseNumber: order.customer?.address?.delivery?.houseNumber,
      addition: order.customer?.address?.delivery?.addition,
      zipCode: order.customer?.address?.delivery?.zipCode,
      city: order.customer?.address?.delivery?.city,
    });
    // changesSummary reads form.getFieldsValue() - force it to recompute now that the
    // form has just been populated, instead of relying on onValuesChange to fire for a
    // programmatic setFieldsValue call (which it doesn't reliably do).
    setFormVersion((v) => v + 1);

    checkOpenSupplement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, order?.id]);

  const checkOpenSupplement = () => {
    setOpenSupplement(undefined);
    setOpenSupplementCheckError(null);
    // Products are read-only for shops without supplement support anyway, so there's no
    // point querying (or surfacing a scary error about) their supplements subcollection.
    if (!PRODUCT_EDITING_SUPPORTED_SHOPS.includes(order?.shopSource)) {
      setOpenSupplement(null);
      return;
    }
    getOpenAdditionalPaymentSupplement(order)
      .then(setOpenSupplement)
      .catch((err) => {
        // Fail closed, not open: if we can't verify whether a payment link is already
        // outstanding, editing products must stay blocked rather than risk a second,
        // conflicting link being created (see "Voorkomen van dubbele/verouderde
        // betaallinks" in the design). Most likely cause: missing Firestore rules/index
        // for the `supplements` subcollection - see browser console for the real error.
        console.error("Error checking for open supplement:", err);
        setOpenSupplementCheckError(err);
        setOpenSupplement(undefined);
      });
  };

  const priceDiff = useMemo(() => {
    if (!order) return { addedItems: [], removedItems: [], amountCents: 0 };
    const { addedItems, removedItems } = diffProductLines(order.products || [], editedProducts);
    const addedTotal = addedItems.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0);
    const removedTotal = removedItems.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0);
    return { addedItems, removedItems, amountCents: addedTotal - removedTotal };
  }, [order, editedProducts]);

  // Live "wat is er gewijzigd" summary across all three tabs, shown below them.
  const changesSummary = useMemo(() => {
    if (!order) return { deliveryChanges: [], customerChanges: [] };
    const values = form.getFieldsValue();
    return {
      deliveryChanges: getDeliveryChanges(order, values),
      customerChanges: getCustomerFieldChanges(order, values),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, formVersion]);

  const handleClose = () => {
    setEditedProducts([]);
    setNewProductId(null);
    setNewVariationWeight(null);
    setNewQuantity(1);
    setOpenSupplement(undefined);
    setOpenSupplementCheckError(null);
    onClose();
  };

  const handleRemoveLine = (key) => {
    setEditedProducts((lines) => lines.filter((line) => lineKey(line) !== key));
  };

  const handleQuantityChange = (key, quantity) => {
    setEditedProducts((lines) => lines.map((line) => (lineKey(line) === key ? { ...line, quantity } : line)));
  };

  const handleAddProduct = () => {
    if (!selectedCatalogProduct || !newQuantity || newQuantity <= 0) return;
    const variation = selectedCatalogProduct.variations?.length
      ? selectedCatalogProduct.variations.find((v) => v.weight === newVariationWeight) ||
        selectedCatalogProduct.variations[0]
      : null;

    const price = variation ? variation.price : selectedCatalogProduct.price || 0;

    const newLine = {
      productId: selectedCatalogProduct.id,
      name: selectedCatalogProduct.name,
      quantity: newQuantity,
      paymentPrice: price,
      // Order details elsewhere (order drawer, packing slips, invoices) read originalPrice -
      // without it those views show an empty/0 price for a line added here. No discount is
      // applied on a CMS-added line, so original and payment price are the same.
      originalPrice: price,
      variation: variation ? { weight: variation.weight } : null,
    };

    setEditedProducts((lines) => {
      const key = lineKey(newLine);
      const existing = lines.find((line) => lineKey(line) === key);
      if (existing) {
        return lines.map((line) => (lineKey(line) === key ? { ...line, quantity: line.quantity + newQuantity } : line));
      }
      return [...lines, newLine];
    });

    setNewProductId(null);
    setNewVariationWeight(null);
    setNewQuantity(1);
  };

  const handleCancelSupplement = async () => {
    setCancelling(true);
    try {
      await cancelSupplementPayment(order, openSupplement.id, openSupplement.payTransactionId);
      message.success(t("supplement_cancelled", "Nabetaling geannuleerd"));
      setOpenSupplement(null);
    } catch (err) {
      console.error("Error cancelling supplement:", err);
      message.error(t("notifications.error_saving"));
    } finally {
      setCancelling(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue();

      // Safety net: re-check for an open supplement right before saving. This catches the
      // case where an earlier save in this same modal session already created one (e.g.
      // the order+supplement saved fine but the notification mail failed, and the admin
      // clicked Opslaan again without closing the modal) - without this, editing products
      // again would silently create a second, duplicate payment link for the same order.
      if (priceDiff.amountCents !== 0 && productEditingSupported) {
        const stillOpen = await getOpenAdditionalPaymentSupplement(order);
        if (stillOpen) {
          setOpenSupplement(stillOpen);
          message.error(
            "Er staat al een nabetaling open voor deze order (waarschijnlijk van een vorige poging). " +
              "Annuleer die eerst, of sluit dit venster en open de order opnieuw.",
          );
          return;
        }
      }

      // 1. Delivery (unchanged behaviour)
      const delivery = { ...order.delivery };
      delivery.date = dayjs(values.deliveryDate).format(DATE_FORMAT_DD_MM_YYYY);
      delivery.dateTimestamp = dayjs(values.deliveryDate).unix();
      delivery.saturdayPickupSelected = dayjs(values.deliveryDate).weekday() === 5;
      delivery.type = values.deliveryType;
      if (values.deliveryType === "pickup" && values.pickupLocation) {
        delivery.pickupLocation = values.pickupLocation;
      }

      // 2. Customer details
      const customer = {
        ...order.customer,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        address: {
          ...order.customer?.address,
          delivery: {
            ...order.customer?.address?.delivery,
            street: values.street,
            houseNumber: values.houseNumber,
            addition: values.addition,
            zipCode: values.zipCode,
            city: values.city,
          },
        },
      };

      const customerChanges = getCustomerFieldChanges(order, values);

      // 3. Products go into the order immediately, independent of any nabetaling -
      // picking/packing/shipping must never wait on a payment being completed. totalPrice
      // must move with them (by the same delta) - it's read all over the CMS as "the
      // order's value" (revenue reports, RFM, exports, the orders table) so it can never
      // be left pointing at the pre-edit amount. What's actually been paid vs. still owed
      // is tracked separately via the `supplements` subcollection, not via totalPrice.
      const totalPrice = (order.totalPrice || 0) + priceDiff.amountCents / 100;
      const success = await updateOrder(order, { delivery, customer, products: editedProducts, totalPrice });
      if (!success) {
        message.error(t("notifications.error_saving"));
        return;
      }

      if (customerChanges.length > 0) {
        await addHistoryEntry(order, {
          adminEmail,
          adminUid,
          action: "customer_updated",
          details: { changes: customerChanges },
        });
      }
      if (priceDiff.addedItems.length > 0 || priceDiff.removedItems.length > 0) {
        await addHistoryEntry(order, {
          adminEmail,
          adminUid,
          action: "products_updated",
          details: { added: priceDiff.addedItems, removed: priceDiff.removedItems },
        });
      }

      // 4. Nabetaling / terugbetaling - only when the net amount actually changed.
      // The order + customer + products save above already succeeded at this point, so
      // anything that goes wrong from here on (payment link, email) must be reported as a
      // distinct warning, never as "saving failed" - the admin must not be led to retry the
      // whole edit and create a second supplement for the exact same change.
      let supplementWarning = null;
      let paymentLinkSent = false;

      // Defensive: the products tab is read-only for shops without supplement support, so
      // priceDiff should already be 0 there - never create a supplement we can't collect on.
      if (priceDiff.amountCents > 0 && productEditingSupported) {
        const description = `Aanvullende betaling voor bestelling ${order.orderId}`;
        const supplementId = await createSupplement(order, {
          type: "additional_payment",
          amountCents: priceDiff.amountCents,
          addedItems: priceDiff.addedItems,
          removedItems: priceDiff.removedItems,
          description: summarizeItems(priceDiff.addedItems) || description,
        });
        // Lock the products tab immediately, in this session too - a second click on
        // Opslaan from here on must never create another supplement for the same edit.
        setOpenSupplement({ id: supplementId, amountCents: priceDiff.amountCents, type: "additional_payment", status: "open" });

        try {
          const paymentUrl = await createPaymentLinkForSupplement(
            order,
            supplementId,
            priceDiff.amountCents,
            description,
          );
          await updateSupplement(order, supplementId, { paymentUrl });
          setOpenSupplement((current) => (current?.id === supplementId ? { ...current, paymentUrl } : current));

          try {
            await sendSupplementPaymentMail(order, {
              orderDocumentId: order.id,
              supplementId,
              customerEmail: customer.email,
              customerFirstName: customer.firstName,
              customerLastName: customer.lastName,
              locale: order.locale || "nl",
              orderReference: order.orderId,
              amountCents: priceDiff.amountCents,
              paymentUrl,
              addedItems: priceDiff.addedItems,
              removedItems: priceDiff.removedItems,
            });
            paymentLinkSent = true;
          } catch (mailErr) {
            console.error("Error sending supplement payment mail:", mailErr);
            supplementWarning =
              `De wijziging is opgeslagen en er staat een betaallink open van ${formatCents(priceDiff.amountCents)}, ` +
              `maar de e-mail naar de klant kon niet verstuurd worden (ook niet na een paar keer proberen). ` +
              `Gebruik "opnieuw versturen" op de Nafacturaties-pagina - maak niet nog een keer dezelfde wijziging.`;
          }
        } catch (paymentErr) {
          console.error("Error creating payment link for supplement:", paymentErr);
          supplementWarning =
            "De wijziging is opgeslagen, maar het aanmaken van de betaallink is mislukt. Open de order opnieuw " +
            "om het via de nabetaling-banner te annuleren en eventueel opnieuw te proberen.";
        }
      } else if (priceDiff.amountCents < 0 && productEditingSupported) {
        await createSupplement(order, {
          type: "refund_due",
          amountCents: Math.abs(priceDiff.amountCents),
          addedItems: priceDiff.addedItems,
          removedItems: priceDiff.removedItems,
          description: summarizeItems(priceDiff.removedItems),
        });
      }

      if (supplementWarning) {
        message.warning(supplementWarning, 12);
      } else if (paymentLinkSent) {
        message.success("Bestelling opgeslagen en de klant heeft een betaallink ontvangen");
      } else {
        message.success(t("order_saved"));
      }
      handleClose();
      await refreshOrders();
    } catch (err) {
      console.error("Error saving order:", err);
      message.error(t("notifications.error_saving"));
    } finally {
      setSaving(false);
    }
  };

  if (!visible || !order) return null;

  const openSupplementCheckPending = openSupplement === undefined && !openSupplementCheckError;
  const productsLocked =
    !productEditingSupported || openSupplementCheckPending || !!openSupplementCheckError || !!openSupplement;

  const columns = [
    {
      title: t("product"),
      dataIndex: "name",
      key: "name",
      render: (name, line) => `${name}${line.variation?.weight ? ` (${line.variation.weight}g)` : ""}`,
    },
    {
      title: t("amount"),
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity, line) => (
        <InputNumber
          min={0}
          value={quantity}
          disabled={productsLocked}
          onChange={(value) => handleQuantityChange(lineKey(line), value)}
        />
      ),
    },
    {
      title: t("price"),
      key: "price",
      render: (_, line) => formatCents(Math.round((line.paymentPrice || 0) * 100) * line.quantity),
    },
    {
      title: "",
      key: "actions",
      render: (_, line) =>
        !productsLocked && (
          <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleRemoveLine(lineKey(line))} />
        ),
    },
  ];

  const noChangesYet =
    changesSummary.deliveryChanges.length === 0 &&
    changesSummary.customerChanges.length === 0 &&
    priceDiff.addedItems.length === 0 &&
    priceDiff.removedItems.length === 0;

  return (
    <Modal
      title={t("edit_order", { orderId: order.orderId })}
      visible={visible}
      onCancel={handleClose}
      onOk={handleSave}
      okText={t("save")}
      cancelText={t("close")}
      confirmLoading={saving}
      width={760}
    >
      <Form layout="vertical" form={form} name="order_edit_form" onValuesChange={() => setFormVersion((v) => v + 1)}>
        <Tabs defaultActiveKey="delivery">
          <Tabs.TabPane tab={t("shipping_method")} key="delivery" forceRender>
            <Form.Item
              name="deliveryType"
              label={t("shipping_method")}
              rules={[{ required: true, message: t("form.choose_delivery_type") }]}
            >
              <Radio.Group onChange={(e) => setDeliveryType(e.target.value)}>
                <Radio value="send">{t("shipping")}</Radio>
                <Radio value="pickup">{t("pickup")}</Radio>
              </Radio.Group>
            </Form.Item>
            {deliveryType === "pickup" && (
              <Form.Item
                name="pickupLocation"
                label={t("pickup_location")}
                rules={[{ required: true, message: "Kies een afhaallocatie" }]}
              >
                <Radio.Group>
                  <Radio value="schepenwijk">Schepenwijk (De Hoek 91a001, Enkhuizen)</Radio>
                  <Radio value="winkel">Winkel (Melkmarkt 10, Enkhuizen)</Radio>
                </Radio.Group>
              </Form.Item>
            )}
            <Form.Item
              name="deliveryDate"
              label={t("delivery_date")}
              rules={[{ required: true, message: t("form.choose_delivery_date") }]}
              className="mb-1"
            >
              <DatePicker
                locale={locale}
                format={DATE_FORMAT_DD_MM_YYYY}
                placeholder={t("delivery_date")}
                showToday={false}
                inputReadOnly
                style={{ width: "100%" }}
                disabledDate={isDeliveryDateDisabled}
                onChange={(value) => setDeliveryDate(value)}
              />
            </Form.Item>
            {deliveryDate && deliveryDate.diff(dayjs(), "days") < 0 && (
              <DateWarningMessage>{t("order_delivery_date_warning_past")}</DateWarningMessage>
            )}
            {deliveryDate && deliveryDate.diff(dayjs(), "days") + 1 > DELIVERY_DATE_WARNING_DAYS && (
              <DateWarningMessage>
                {t("order_delivery_date_warning", { days: deliveryDate.diff(dayjs(), "days") + 1 })}
              </DateWarningMessage>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="Klantgegevens" key="customer" forceRender>
            <Form.Item name="firstName" label="Voornaam">
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Achternaam">
              <Input />
            </Form.Item>
            <Form.Item name="email" label="E-mailadres" rules={[{ type: "email", message: "Ongeldig e-mailadres" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Telefoonnummer">
              <Input />
            </Form.Item>
            <Form.Item name="street" label="Straat">
              <Input />
            </Form.Item>
            <Form.Item name="houseNumber" label="Huisnummer">
              <Input />
            </Form.Item>
            <Form.Item name="addition" label="Toevoeging">
              <Input />
            </Form.Item>
            <Form.Item name="zipCode" label="Postcode">
              <Input />
            </Form.Item>
            <Form.Item name="city" label="Plaats">
              <Input />
            </Form.Item>
          </Tabs.TabPane>

          <Tabs.TabPane tab={t("product")} key="products" forceRender>
            {!productEditingSupported && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Producten aanpassen kan nog niet voor ${order.shopName || order.shopSource}-bestellingen`}
                description="Een productwijziging kan een nabetaling opleveren, en die kan voor deze shop nog niet automatisch verwerkt worden. Zodra dat is ingebouwd, kan het hier ook. Klantgegevens en bezorging kun je wel gewoon aanpassen."
              />
            )}

            {productEditingSupported && openSupplementCheckPending && <p>Nabetalingen controleren...</p>}

            {openSupplementCheckError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="Kon niet controleren of er al een openstaande nabetaling is"
                description={
                  <>
                    <p style={{ marginBottom: 8 }}>
                      Producten blijven voorlopig geblokkeerd om te voorkomen dat er per ongeluk een tweede
                      betaallink ontstaat. Meestal betekent dit dat de Firestore rules/index voor de{" "}
                      <code>supplements</code>-subcollectie nog niet kloppen - zie de browser console voor het
                      volledige foutbericht.
                    </p>
                    <Button onClick={checkOpenSupplement}>Opnieuw proberen</Button>
                  </>
                }
              />
            )}

            {!!openSupplement && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Openstaande nabetaling van ${formatCents(openSupplement.amountCents)}`}
                description={
                  <>
                    <p style={{ marginBottom: 8 }}>
                      Er staat al een betaallink open voor deze order. Rond deze eerst af (annuleer 'm hieronder)
                      voordat je de producten opnieuw aanpast. Let op: de klant kan de link mogelijk nog heel even
                      gebruiken na annuleren.
                    </p>
                    <Button danger loading={cancelling} onClick={handleCancelSupplement}>
                      Annuleer deze nabetaling
                    </Button>
                  </>
                }
              />
            )}

            <Table
              dataSource={editedProducts.map((line) => ({ ...line, key: lineKey(line) }))}
              columns={columns}
              pagination={false}
              size="small"
            />

            {!productsLocked && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-end" }}>
                <Select
                  showSearch
                  placeholder="Product toevoegen"
                  style={{ flex: 1 }}
                  value={newProductId}
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? "").toLowerCase().includes(input.toLowerCase())}
                  onChange={(value) => {
                    setNewProductId(value);
                    setNewVariationWeight(null);
                  }}
                >
                  {shopProducts.map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.name}
                    </Select.Option>
                  ))}
                </Select>
                {selectedCatalogProduct?.variations?.length > 0 && (
                  <Select
                    placeholder="Variant"
                    style={{ width: 140 }}
                    value={newVariationWeight}
                    onChange={setNewVariationWeight}
                  >
                    {selectedCatalogProduct.variations.map((variation) => (
                      <Select.Option key={variation.weight} value={variation.weight}>
                        {variation.weight}g - {formatCents(Math.round(variation.price * 100))}
                      </Select.Option>
                    ))}
                  </Select>
                )}
                <InputNumber min={1} value={newQuantity} onChange={setNewQuantity} style={{ width: 80 }} />
                <Button icon={<PlusOutlined />} onClick={handleAddProduct} disabled={!newProductId} />
              </div>
            )}

            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Huidig totaal</span>
              <span>{formatCents(Math.round((order.totalPrice || 0) * 100))}</span>
            </div>
            {priceDiff.amountCents !== 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
                <span>Nieuw totaal na opslaan</span>
                <span>{formatCents(Math.round((order.totalPrice || 0) * 100) + priceDiff.amountCents)}</span>
              </div>
            )}
          </Tabs.TabPane>
        </Tabs>
      </Form>

      <Divider orientation="left">Samenvatting van wijzigingen</Divider>
      {noChangesYet && <p style={{ color: "#888" }}>Nog geen wijzigingen.</p>}

      {changesSummary.deliveryChanges.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <strong>Verzending</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {changesSummary.deliveryChanges.map((change) => (
              <li key={change.field}>
                {change.field}: {change.from || "-"} → {change.to}
              </li>
            ))}
          </ul>
        </div>
      )}

      {changesSummary.customerChanges.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <strong>Klantgegevens</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {changesSummary.customerChanges.map((change) => (
              <li key={change.field}>
                {CUSTOMER_FIELD_LABELS[change.field]}: {change.from || "-"} → {change.to || "-"}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(priceDiff.addedItems.length > 0 || priceDiff.removedItems.length > 0) && (
        <div style={{ marginBottom: 12 }}>
          <strong>Producten</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {priceDiff.addedItems.map((item, index) => (
              <li key={`added-${index}`} style={{ color: "#2e7d32" }}>
                + {item.quantity}x {item.name}
                {item.variation ? ` (${item.variation}g)` : ""}
              </li>
            ))}
            {priceDiff.removedItems.map((item, index) => (
              <li key={`removed-${index}`} style={{ color: "#B02418" }}>
                - {item.quantity}x {item.name}
                {item.variation ? ` (${item.variation}g)` : ""}
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontWeight: "bold" }}>
            <span>Prijsverschil</span>
            <span>
              {priceDiff.amountCents > 0 ? "+" : ""}
              {formatCents(priceDiff.amountCents)}
            </span>
          </div>
          {priceDiff.amountCents > 0 && (
            <p style={{ color: "#B02418", marginTop: 4 }}>
              Bij opslaan wordt er een nabetaling van {formatCents(priceDiff.amountCents)} aangemaakt en automatisch
              een betaallink naar de klant gemaild.
            </p>
          )}
          {priceDiff.amountCents < 0 && (
            <p style={{ color: "#B02418", marginTop: 4 }}>
              Bij opslaan wordt {formatCents(Math.abs(priceDiff.amountCents))} als "te veel betaald" geregistreerd op
              het nafacturatie-overzicht - terugbetaling blijft een handmatige actie.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OrderEditModal;
